import React, { useState, useEffect } from 'react';
import { getChangeOrders, createChangeOrder, updateChangeOrder, deleteChangeOrder } from '../api';
import { useTheme } from '../hooks/useTheme';
import ConfirmDialog from './ConfirmDialog';
import LoadingState from './LoadingState';
import useConfirm from '../hooks/useConfirm';

const ChangeOrdersTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [changeOrders, setChangeOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [approvedAmount, setApprovedAmount] = useState('');
  // Double-submit guards. `creating` covers the New CO form, `approving`
  // covers the Approve-with-amount modal, `statusChangingId` tracks the
  // CO id whose Submit/Reject inline button is in flight (per-row so
  // other rows stay clickable), and `deletingId` does the same for
  // the per-row Delete button.
  const [creating, setCreating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    change_order_number: '',
    title: '',
    description: '',
    requested_amount: '',
    notes: ''
  });

  const fetchChangeOrders = async () => {
    try {
      setLoading(true);
      const data = await getChangeOrders(projectId);
      setChangeOrders(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch change orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangeOrders();
  }, [projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateChangeOrder = async (e) => {
    e.preventDefault();
    if (creating) return;
    try {
      setCreating(true);
      await createChangeOrder({
        ...formData,
        project_id: projectId,
        status: 'Draft'
      });
      setFormData({
        change_order_number: '',
        title: '',
        description: '',
        requested_amount: '',
        notes: ''
      });
      setShowNewModal(false);
      await fetchChangeOrders();
      refreshSummary();
    } catch (err) {
      setError('Failed to create change order');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (changeOrder, newStatus) => {
    if (newStatus === 'Approved') {
      setSelectedChangeOrder(changeOrder);
      setApprovedAmount(changeOrder.requested_amount || '');
      setShowApproveModal(true);
    } else {
      if (statusChangingId) return;
      try {
        setStatusChangingId(changeOrder.id);
        await updateChangeOrder(changeOrder.id, {
          status: newStatus
        });
        await fetchChangeOrders();
        refreshSummary();
      } catch (err) {
        setError('Failed to update change order status');
        console.error(err);
      } finally {
        setStatusChangingId(null);
      }
    }
  };

  const handleApproveWithAmount = async () => {
    if (approving) return;
    try {
      setApproving(true);
      await updateChangeOrder(selectedChangeOrder.id, {
        status: 'Approved',
        approved_amount: parseFloat(approvedAmount)
      });
      setShowApproveModal(false);
      setApprovedAmount('');
      setSelectedChangeOrder(null);
      await fetchChangeOrders();
      refreshSummary();
    } catch (err) {
      setError('Failed to approve change order');
      console.error(err);
    } finally {
      setApproving(false);
    }
  };

  const handleDeleteChangeOrder = async (changeOrderId) => {
    if (deletingId) return;
    const ok = await confirm({ title: 'Delete Change Order', message: 'Are you sure you want to delete this change order? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (ok) {
      try {
        setDeletingId(changeOrderId);
        await deleteChangeOrder(changeOrderId);
        await fetchChangeOrders();
        refreshSummary();
      } catch (err) {
        setError('Failed to delete change order');
        console.error(err);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Draft':
        return 'badge badge-secondary';
      case 'Submitted':
        return 'badge badge-warning';
      case 'Approved':
        return 'badge badge-success';
      case 'Rejected':
        return 'badge badge-error';
      default:
        return 'badge badge-secondary';
    }
  };

  if (loading) {
    return <LoadingState message="Loading change orders…" />;
  }

  return (
    <div className="container">
      <div className="header-section">
        <h2>Change Orders</h2>
        <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
          New Change Order
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>CO#</th>
              <th>Title</th>
              <th>Status</th>
              <th>Requested Amount</th>
              <th>Approved Amount</th>
              <th>Submitted Date</th>
              <th>Approved Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {changeOrders.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                  No change orders yet.
                </td>
              </tr>
            ) : (
              changeOrders.map(co => (
                <tr key={co.id}>
                  <td><strong>{co.change_order_number}</strong></td>
                  <td>{co.title}</td>
                  <td>
                    <span className={getStatusBadgeClass(co.status)}>
                      {co.status}
                    </span>
                  </td>
                  <td>{formatCurrency(co.requested_amount)}</td>
                  <td>{formatCurrency(co.approved_amount)}</td>
                  <td>{formatDate(co.submitted_date)}</td>
                  <td>{formatDate(co.approved_date)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {co.status === 'Draft' && (
                        <>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleStatusChange(co, 'Submitted')}
                            disabled={statusChangingId === co.id || deletingId === co.id}
                            style={{
                              cursor: statusChangingId === co.id ? 'wait' : undefined,
                              opacity: (statusChangingId === co.id || deletingId === co.id) ? 0.7 : 1,
                            }}
                          >
                            {statusChangingId === co.id ? 'Submitting…' : 'Submit'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteChangeOrder(co.id)}
                            disabled={deletingId === co.id || statusChangingId === co.id}
                            style={{
                              cursor: deletingId === co.id ? 'wait' : undefined,
                              opacity: (deletingId === co.id || statusChangingId === co.id) ? 0.7 : 1,
                            }}
                          >
                            {deletingId === co.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </>
                      )}
                      {co.status === 'Submitted' && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleStatusChange(co, 'Approved')}
                            disabled={statusChangingId === co.id}
                            style={{
                              cursor: statusChangingId === co.id ? 'wait' : undefined,
                              opacity: statusChangingId === co.id ? 0.7 : 1,
                            }}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusChange(co, 'Rejected')}
                            disabled={statusChangingId === co.id}
                            style={{
                              cursor: statusChangingId === co.id ? 'wait' : undefined,
                              opacity: statusChangingId === co.id ? 0.7 : 1,
                            }}
                          >
                            {statusChangingId === co.id ? 'Rejecting…' : 'Reject'}
                          </button>
                        </>
                      )}
                      {co.status === 'Approved' && (
                        <span style={{ padding: '4px 8px', color: t.text }}>
                          Approved
                        </span>
                      )}
                      {co.status === 'Rejected' && (
                        <span style={{ padding: '4px 8px', color: t.textSecondary }}>
                          Rejected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Change Order Modal */}
      {showNewModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>New Change Order</h3>
              <button className="btn-close" onClick={() => setShowNewModal(false)} disabled={creating}>×</button>
            </div>
            <form onSubmit={handleCreateChangeOrder}>
              <div className="form-group">
                <label>Change Order Number *</label>
                <input
                  type="text"
                  name="change_order_number"
                  value={formData.change_order_number}
                  onChange={handleInputChange}
                  placeholder="e.g., CO-001"
                  required
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Additional Materials"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Describe the change order details..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Requested Amount *</label>
                <input
                  type="number"
                  name="requested_amount"
                  value={formData.requested_amount}
                  onChange={handleInputChange}
                  step="0.01"
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowNewModal(false)}
                  disabled={creating}
                  style={{
                    cursor: creating ? 'not-allowed' : undefined,
                    opacity: creating ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                  style={{
                    cursor: creating ? 'wait' : undefined,
                    opacity: creating ? 0.8 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {creating && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {creating ? 'Creating…' : 'Create Change Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedChangeOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Approve Change Order</h3>
              <button className="btn-close" onClick={() => setShowApproveModal(false)} disabled={approving}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <p><strong>Change Order:</strong> {selectedChangeOrder.change_order_number}</p>
              <p><strong>Title:</strong> {selectedChangeOrder.title}</p>
              <p><strong>Requested Amount:</strong> {formatCurrency(selectedChangeOrder.requested_amount)}</p>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Approved Amount *</label>
                <input
                  type="number"
                  value={approvedAmount}
                  onChange={(e) => setApprovedAmount(e.target.value)}
                  step="0.01"
                  required
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowApproveModal(false)}
                  disabled={approving}
                  style={{
                    cursor: approving ? 'not-allowed' : undefined,
                    opacity: approving ? 0.6 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleApproveWithAmount}
                  disabled={approving}
                  style={{
                    cursor: approving ? 'wait' : undefined,
                    opacity: approving ? 0.8 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {approving && (
                    <span style={{
                      width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite', display: 'inline-block',
                    }} />
                  )}
                  {approving ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ChangeOrdersTab;
